import { graphql, withApollo } from "@apollo/client/react/hoc"
import Card from "@pathwright/ui/src/components/card/Card"
import View from "@pathwright/ui/src/components/ui/View"
import debounce from "lodash/debounce"
import compose from "lodash/flowRight"
import get from "lodash/get"
import moment from "moment"
import PropTypes from "prop-types"
import { Component } from "react"

import withTranslate from "@pathwright/ux/src/components/lng/withTranslate"
import styled from "styled-components"
import { LicenseOfferingDocument } from "@pathwright/graphql-client/src/generated"
import LazyAdaptedAuthContainer from "../../auth/LazyAdaptedAuthContainer"
import COUPON_QUERY from "../../coupon/graphql/coupon-query"
import OrderConfirmationContainer from "../../order/OrderConfirmationContainer"
import {
  PathwrightContext,
  withPathwrightContext
} from "../../pathwright/PathwrightContext"
import { flattenEdges } from "../../utils/apollo"
import { withContextAsProp } from "../../utils/component"
import LicenseResourceForm from "./LicenseResourceForm"
import LicenseResourceSchoolGate from "./LicenseResourceSchoolGate"
import LicenseResourceSuccess from "./LicenseResourceSuccess"
import {
  STEP_AUTH,
  STEP_CREATED,
  STEP_LICENSE,
  STEP_ORDER_CONFIRMATION,
  STEP_SCHOOL
} from "./constants"
import LicenseResourceHeader from "./shared/LicenseResourceHeader"
import { getMinSeats } from "./utils"

const StyledCard = styled(Card)`
  .LicenseResourceForm {
    padding: 1em;
  }
`

class LicenseResourceFlow extends Component {
  constructor(props) {
    super(props)
    this.debouncedUpdateDiscount = debounce(this.applyDiscountCode, 500)
    this.state = {
      licenseeSchool: null,
      seatCount: null,
      discountCode: null,
      computedDiscount: null,
      createdLicense: null
    }
  }

  UNSAFE_componentWillMount() {
    // can skip STEP_SCHOOL if licenseeSchool is already set
    // (e.g. licensing through Belltower from Library)
    if (this.props.licenseeSchool) {
      this.setState({ licenseeSchool: this.props.licenseeSchool })
    }
  }

  get step() {
    if (this.props.orderId) {
      return STEP_ORDER_CONFIRMATION // order successfully created
    }
    if (!this.props.context.me) {
      return STEP_AUTH // need to sign in to user account
    }
    if (!this.state.licenseeSchool) {
      return STEP_SCHOOL // need to choose a licensee school account
    }
    if (this.state.createdLicense) {
      return STEP_CREATED // license successfully created
    }
    return STEP_LICENSE // need to complete license purchase form
  }

  selectSchool = (licenseeSchool) => {
    this.setState({ licenseeSchool })
  }

  get licensorSchool() {
    return this.props.licensorSchool || this.props.context.school
  }

  // Get at least the minimum number of seats to license
  get seatCount() {
    return Math.max(
      this.state.seatCount,
      getMinSeats(this.props.license_offering)
    )
  }

  setSeatCount = (seatCount) => {
    this.setState({ seatCount }, this.debouncedUpdateDiscount)
  }

  // TODO: move this to the DiscountForm component
  applyDiscountCode = async (code) => {
    if (!(code ||= this.state.discountCode)) {
      return
    }

    try {
      const { data } = await this.props.client.query({
        query: COUPON_QUERY,
        variables: {
          code,
          seat_count: this.seatCount,
          resource_license_offering_id: this.props.license_offering.id
        },
        fetchPolicy: "network-only"
      })

      const coupon = data?.school?.coupon

      if (!coupon) {
        throw new Error("Invalid discount code.")
      }

      if (coupon.user_can_redeem) {
        this.setState({ computedDiscount: data.school.coupon })
        this.setState({ discountCode: code })
        return this.props.t("payments.elements_form.code_discount_applied", {
          code: coupon.code
        })
      }

      if (coupon.is_one_per_user && coupon.user_redeemed) {
        throw new Error("You have already redeemed this discount code.")
      }

      if (!coupon.has_started) {
        throw new Error(
          `This discount code is not redeemable until ${moment(
            coupon.start_date
          ).format("MMMM Do YYYY")}.`
        )
      }

      if (coupon.has_ended) {
        throw new Error(
          `This discount code expired on ${moment(coupon.end_date).format(
            "MMMM Do YYYY"
          )}.`
        )
      }
    } catch (error) {}

    // Somehow still not redeemable?
    throw new Error("Invalid discount code.")
  }

  clearDiscountCode = () => {
    this.setState({ computedDiscount: null, discountCode: null })
  }

  // We weren't even calling this.
  // onLicenseSuccess = (resourceLicense, amount) => {
  //   this.props.onLicenseSuccess && this.props.onLicenseSuccess(resourceLicense)

  //   // Track transaction and member-created cohort events when user is licensing from and to the same school
  //   if (this.state.licenseeSchool.id === this.licensorSchool.id) {
  //     const { me } = this.props.context
  //     const { computedDiscount: discount, discountCode } = this.state

  //     const data = {
  //       transaction: {
  //         id: null, // ideally the stripe transaction id, but we don't even track this in our DB for member-created groups
  //         method: resourceLicense.license_offering.included_in_user_subscription
  //           ? "subscription"
  //           : amount
  //           ? "payment"
  //           : "free",
  //         amount: amount || 0,
  //         discount: discount
  //           ? {
  //               code: discountCode,
  //               amount: discount.discount_amount,
  //               percent: discount.discount_percent,
  //               maxSeatDiscount: discount.max_seat_discount
  //             }
  //           : null
  //       },
  //       user: {
  //         id: me.id,
  //         email: me.email,
  //         firstName: me.first_name,
  //         lastName: me.last_name,
  //         fullName: me.full_name
  //       },
  //       product: {
  //         cohort: {
  //           id: resourceLicense.group.id,
  //           name: resourceLicense.group.name
  //         },
  //         resource: {
  //           id: resourceLicense.group.resource.id,
  //           name: resourceLicense.group.resource.name
  //         },
  //         resourceLicense: {
  //           id: resourceLicense.id,
  //           seatsCount: resourceLicense.seat_count,
  //           seatsFilled: resourceLicense.seats_filled,
  //           seatsAvailable: resourceLicense.seats_available,
  //           expirationDate: resourceLicense.expiration_dtime
  //         },
  //         licenseOffering: {
  //           id: resourceLicense.license_offering.id,
  //           costPerSeat: resourceLicense.license_offering.cost_per_seat,
  //           includeCreatorSeat:
  //             resourceLicense.license_offering.include_creator_seat,
  //           includedInUserSubscription:
  //             resourceLicense.license_offering.included_in_user_subscription,
  //           minimumSeatCount:
  //             resourceLicense.license_offering.minimum_seat_count
  //         }
  //       }
  //     }

  //     if (amount > 0) {
  //       dispatchGlobalEvent(TRANSACTION_EVENT, {
  //         ...data.transaction,
  //         user: data.user,
  //         product: data.product
  //       })
  //     }
  //     dispatchGlobalEvent(MEMBER_COHORT_CREATED_EVENT, {
  //       transaction: data.transaction,
  //       user: data.user,
  //       ...data.product.cohort,
  //       ...omit(data.product, "cohort")
  //     })
  //   }
  // }

  renderStep() {
    switch (this.step) {
      case STEP_AUTH:
        return (
          <LazyAdaptedAuthContainer
            // Refetch license offering after auth — pricing / subscription status may change.
            onComplete={this.props.refetchLicenseOfering}
          />
        )
      case STEP_SCHOOL:
        return (
          <LicenseResourceSchoolGate
            license_offering={this.props.license_offering}
            onSelectSchool={this.selectSchool}
            me={this.props.context.me}
          />
        )
      case STEP_LICENSE:
        return (
          <LicenseResourceForm
            licenseOffering={this.props.license_offering}
            licensorSchool={this.licensorSchool}
            licenseeSchool={this.state.licenseeSchool}
            computedDiscount={this.state.computedDiscount}
            applyDiscountCode={this.applyDiscountCode}
            clearDiscountCode={this.clearDiscountCode}
            discountCode={this.state.discountCode}
            canGoBack={!this.props.licenseeSchool}
            seatCount={this.seatCount}
            setSeatCount={this.setSeatCount}
            onBack={() => this.setState({ licenseeSchool: null })}
            refetchLicenseOffering={this.props.refetchLicenseOfering}
            // Cover Offer CTA passes free|one_off|subscription; ResourceAbout omits it.
            paymentType={this.props.paymentType}
            // for non-order licensing
            onSuccess={(licenseResource) => {
              // refetching context mainly in order to update the user's group_role_stats
              // which controls what Home UIs are available to the user
              this.props.context &&
                this.props.context.query &&
                // this.props.context should always be present, just being cautious
                this.props.context.query.refetch()
              this.setState({ createdLicense: licenseResource })
            }}
            redirectTo={this.props.redirectTo || window.location.href}
          />
        )
      case STEP_CREATED:
        return (
          <LicenseResourceSuccess
            licenseeSchool={this.state.licenseeSchool}
            license_offering={this.props.license_offering}
            license={this.state.createdLicense || {}}
            card={this.props.card}
            onLicenseSuccess={this.props.onLicenseSuccess}
          />
        )
      case STEP_ORDER_CONFIRMATION:
        return (
          <OrderConfirmationContainer
            orderId={this.props.orderId}
            onOrderFulfilled={this.props.onOrderFulfilled}
          />
        )
      default:
        return null
    }
  }

  render() {
    const { card, license_offering } = this.props
    const hasStep = !!this.step && !!license_offering

    return card ? (
      <StyledCard card={card} navless noaction>
        {hasStep && this.step !== STEP_CREATED && (
          <LicenseResourceHeader
            card={card}
            resource={license_offering.resource}
            license_offering={license_offering}
            licenseeSchool={this.state.licenseeSchool}
            computedDiscount={this.state.computedDiscount}
            seatCount={this.seatCount}
          />
        )}
        <View roundedBottom>{hasStep && this.renderStep()}</View>
      </StyledCard>
    ) : hasStep ? (
      this.renderStep()
    ) : null
  }
}

LicenseResourceFlow.displayName = "LicenseResourceFlow"

LicenseResourceFlow.propTypes = {
  // the resource license offering id
  id: PropTypes.number.isRequired,
  // optionally scope query to another school
  schoolId: PropTypes.number,
  // used to set stripe key
  licensorSchool: PropTypes.object,
  // optional, used to skip licensee school step
  // and is also used to determine whether this is school or group licensing (see isSchoolLicensing)
  licenseeSchool: PropTypes.object,
  // props from query, mutation
  license_offering: PropTypes.object,
  // Optional order id from mutation
  orderId: PropTypes.number,
  // Cover Offer CTA: free | one_off | subscription. Omit for ResourceAbout.
  paymentType: PropTypes.string,
  onOrderFulfilled: PropTypes.func
}

export default compose(
  withTranslate,
  withApollo,
  withPathwrightContext,
  withContextAsProp(PathwrightContext, "schoolId", "school.id"),
  graphql(LicenseOfferingDocument, {
    options: ({ id, schoolId }) => ({
      variables: {
        id: parseInt(id), // RLO id
        school_id: parseInt(schoolId)
      }
    }),
    props: ({ data }) => ({
      error: data.error,
      loading: data.loading,
      license_offering: flattenEdges(
        get(data, "context.school.license_offering")
      ),
      refetchLicenseOfering: data.refetch
    })
  })
)(LicenseResourceFlow)
